/**
 * パズルのコイン報酬(docs/11_thinking_module.md §3)。
 *
 * **コインを実際に増やせるのは Cloud Functions `submitPuzzleResult` だけ**
 * (CLAUDE.md ルール2)。クライアントは「1セッション分の記録を送って、
 * 返ってきた付与額と残高を表示する」ことしかしない。
 *
 * ローカルモード(Firebase 未設定)では送信先そのものが無いので、
 * `NoPuzzleRewardService` が `null` を返し、
 * **画面はコインの表示を丸ごと出さない**(「0コイン」とも出さない)。
 *
 * ここは `factory.ts` から静的に読まれるので、`@paltyping/puzzle` は
 * **型としてだけ**参照する(`import type`)。値を import すると
 * パズルの遅延読込チャンクが初期バンドルに載ってしまう(docs/11 §4)。
 */
import type { PuzzleGenre } from '@paltyping/puzzle';

/** 1問ぶんの記録(callable `submitPuzzleResult` の entries 要素と同じ形)。 */
export interface PuzzleRewardEntry {
  /** 出題に使ったシード。 */
  readonly seed: number;
  /** 最終的に解けたか。 */
  readonly solved: boolean;
  /** **1回めのこたえあわせで**解けたか(報酬の対象はこれ)。 */
  readonly firstTry: boolean;
  /** その問題にかかった時間(ms)。 */
  readonly elapsedMs: number;
  /** その問題で使ったヒント数。 */
  readonly hintsUsed: number;
  /** この問題の結果でレベルが上がったか。 */
  readonly promoted: boolean;
}

/** 1セッション分の送信内容。 */
export interface PuzzleSessionSubmission {
  readonly genre: PuzzleGenre;
  /** **セッション開始時**のレベル(途中で昇格しても変えない)。 */
  readonly level: number;
  readonly entries: readonly PuzzleRewardEntry[];
  /** セッション開始時刻(epoch ms)。 */
  readonly sessionStartedAt: number;
}

/** サーバーが返した付与結果。 */
export interface PuzzleRewardReceipt {
  /** 今回もらった puzzle コイン。 */
  readonly coinsEarned: number;
  /** 付与後の puzzle コイン残高。 */
  readonly balance: number;
  /** 1日の上限で削られたか。 */
  readonly capped: boolean;
  /**
   * 今回もらった**がんばりポイント**(docs/12 §2 のリーグ軸)。
   * コインの日次上限とは別の物差しなので、コインが止まってもこちらは入る。
   */
  readonly effortEarned: number;
}

/**
 * パズル報酬の窓口。
 *
 * どのメソッドも **`null` = 「わからない / 送らない」**。
 * 呼び出し側は `null` のときコイン表示そのものを出さない
 * (子どもの画面に「0」や「エラー」を出して体験を止めないため)。
 */
export interface PuzzleRewardService {
  /**
   * セッションの記録を送って付与結果を受け取る。
   *
   * 通信できなければ静かに保留して `null` を返す(次回の送信でまとめて流す)。
   * **まとめ画面はこの結果を待たない**。返ってきたら差し替えるだけ。
   */
  submitSession(input: PuzzleSessionSubmission): Promise<PuzzleRewardReceipt | null>;
  /** いまの puzzle コイン残高(不明・ローカルモードは `null`)。 */
  balance(): Promise<number | null>;
}

/**
 * ローカルモード用の何もしない実装。
 *
 * パズルは Firebase 未設定でも普通に遊べる(適応レベルは localStorage)。
 * コインだけが付かないので、画面はコイン表示を隠す。
 */
export class NoPuzzleRewardService implements PuzzleRewardService {
  async submitSession(): Promise<PuzzleRewardReceipt | null> {
    return null;
  }

  async balance(): Promise<number | null> {
    return null;
  }
}
